import { useEffect, useState } from "react";
import { isPrerendering } from '@/utils/isPrerendering';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Users, Mail } from "lucide-react";

export const CommunityPopup = () => {
  // Do not render the popup at all during the prerendering process.
  if (isPrerendering()) {
    return null;
  }
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const hasSeenPopup = localStorage.getItem("hasSeenCommunityPopup");
    if (!hasSeenPopup) {
      // Show popup after 5 seconds for first-time visitors
      const timer = setTimeout(() => setIsOpen(true), 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem("hasSeenCommunityPopup", "true");
  };

  const handleJoinDiscord = () => {
    window.open('https://discord.gg/KGXz6Y3zuT', '_blank');
    handleClose();
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase.functions.invoke('subscribe-newsletter', {
        body: { email }
      });

      if (error) throw error;
      
      toast.success("Successfully subscribed! Check your email for a free shipping code.");
      handleClose();
    } catch (error: any) {
      console.error("Newsletter subscription error:", error);
      toast.error(error.message || "Failed to subscribe. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md bg-gaming-dark border-gaming-light-gray/20 text-white">
        <DialogHeader>
          <DialogTitle className="text-center flex flex-col items-center gap-3">
            <Users className="h-12 w-12 text-gaming-blue" />
            <span className="text-2xl font-bold">Join Our Community!</span>
          </DialogTitle>
          <DialogDescription className="text-center text-gray-400 pt-2">
            Get exclusive deals, content, and be the first to know about new drops.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 py-4">
          <form onSubmit={handleNewsletterSubmit} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="email"
                placeholder="Enter your email for a free shipping code"
                className="bg-gaming-darker border-gaming-light-gray/30 pl-10"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-gaming-blue hover:bg-gaming-blue/80"
              disabled={isLoading}
            >
              {isLoading ? "Subscribing..." : "Subscribe & Get Code"}
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-gaming-light-gray/30" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-gaming-dark px-2 text-gray-400">
                or
              </span>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white border-indigo-500"
            onClick={handleJoinDiscord}
          >
            Join our Discord Server
          </Button>
        </div>
        <div className="text-center">
            <Button
                type="button"
                variant="link"
                className="text-gray-400 hover:text-white"
                onClick={handleClose}
              >
                Maybe Later
            </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
